import type { ReactNode } from 'react';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { Pagination } from './Pagination';
import { SearchInput } from './SearchInput';
import type { Pagination as PaginationMeta } from '@/types/api';

interface ListPageProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Primary action, e.g. a "New …" button. Callers omit it when unpermitted. */
  action?: ReactNode;
  search?: { value: string; onChange: (value: string) => void; placeholder?: string };
  /** Extra filter controls rendered next to the search box. */
  filters?: ReactNode;
  pagination?: PaginationMeta | undefined;
  onPageChange?: (page: number) => void;
  onSizeChange?: (size: number) => void;
  children: ReactNode;
}

/**
 * The common shape of every list screen: title, toolbar, table, pagination.
 *
 * A shared shell rather than a configuration-driven "generic CRUD page". Each
 * feature still writes its own columns, filters and forms — those are where the
 * screens genuinely differ, and forcing them through a config object would trade
 * a little duplication for an abstraction nobody can read.
 */
export function ListPage({
  title,
  description,
  icon,
  action,
  search,
  filters,
  pagination,
  onPageChange,
  onSizeChange,
  children,
}: ListPageProps) {
  const hasToolbar = search !== undefined || filters !== undefined;

  return (
    <div>
      <PageHeader title={title} description={description} action={action} icon={icon} />

      <Card className="overflow-hidden">
        {hasToolbar && (
          <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surface-2/60 px-4 py-3">
            {search !== undefined && (
              <SearchInput
                value={search.value}
                onChange={search.onChange}
                placeholder={search.placeholder}
              />
            )}
            {filters}
          </div>
        )}

        {children}

        {onPageChange !== undefined && (
          <Pagination
            pagination={pagination}
            onPageChange={onPageChange}
            onSizeChange={onSizeChange}
          />
        )}
      </Card>
    </div>
  );
}

/** Compact select used for the inline filters in a list toolbar. */
export function FilterSelect<T extends string>({ label, value, onChange, options }: {
  label: string;
  value: T | '';
  onChange: (value: T | '') => void;
  options: { value: T; label: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as T | '')}
        className="rounded-lg border border-line bg-card py-2 pl-3 pr-8 text-sm text-ink shadow-xs transition-colors hover:border-line-strong focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)]"
      >
        <option value="">{label}: all</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
